// # Mentee Stats Component for Step 1
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaChartLine, FaBook, FaCalendarAlt, FaChartPie, FaExclamationTriangle, FaBrain, FaHistory } from 'react-icons/fa';
import DashboardHeader from '../../../components/DashboardHeader';
import DashboardFooter from '../../../components/DashboardFooter';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { useAuthStore } from '../../../auth/state';
import cacheManager from '../../../auth/cache_manager';
import QBankStats from './stats1_qbnk';
import LibraryStats from './stats1_lib';
import WeaknessesStats from './stats1_weak';
import ExamDayStats from './stats1_examday';
import PredictionStats from './stats1_predict';
import Stats1Overview from './stats1_overview';
import ReviewStats from './stats1_review';
import { getBasePath, getTargetUserId } from '../m1_access';
import './mentee_stats1.css';

// # Stats component for Step 1 mentees
function MenteeStats1({ isAdmin, isTutor, isMentorView }) {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [targetUserId, setTargetUserId] = useState(null);

  // # Get target user ID and base path
  useEffect(() => {
    const viewingMenteeId = sessionStorage.getItem('currentViewingMenteeId');
    const userId = getTargetUserId({ 
      isAdmin, 
      isTutor, 
      viewingMenteeId, 
      currentUserId: user?.uid 
    });
    setTargetUserId(userId);
  }, [user?.uid, isAdmin, isTutor]);

  // # Define tabs configuration
  const tabs = [
    { id: 'overview', label: 'Overview', icon: FaChartLine },
    { id: 'qbank', label: 'Qbank Stats', icon: FaBrain },
    { id: 'exam', label: 'Exam Day', icon: FaCalendarAlt },
    { id: 'predictions', label: 'Predictions', icon: FaChartPie },
    { id: 'library', label: 'Library Stats', icon: FaBook },
    { id: 'reviews', label: 'Review Stats', icon: FaHistory },
    { id: 'weaknesses', label: 'Weaknesses', icon: FaExclamationTriangle },
  ];

  // # Load theme preference from user data on initial render
  useEffect(() => {
    const loadThemePreference = async () => {
      if (user && user.uid) {
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData && userData.themes && userData.themes.gen_theme !== undefined) {
          setIsDarkMode(userData.themes.gen_theme === 'dark');
        }
      }
    };
    
    loadThemePreference();
  }, [user]);

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      if (user && user.uid) {
        await updateDoc(doc(db, 'users', user.uid), {
          'themes.gen_theme': newTheme ? 'dark' : 'light'
        });
        
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData) {
          const updatedUserData = {
            ...userData,
            themes: {
              ...(userData.themes || {}),
              gen_theme: newTheme ? 'dark' : 'light'
            }
          };
          cacheManager.set(`user_${user.uid}`, updatedUserData);
        }
      }
    } catch (error) {
      console.error('Error updating theme preference:', error);
    }
  };

  // # Render tab content based on active tab
  const renderTabContent = () => {
    const commonProps = {
      isAdmin,
      isTutor,
      isMentorView,
      isDarkMode,
      targetUserId
    };

    switch (activeTab) {
      case 'overview':
        return <Stats1Overview {...commonProps} />;
      case 'qbank':
        return <QBankStats {...commonProps} />;
      case 'library':
        return <LibraryStats {...commonProps} />;
      case 'reviews':
        return <ReviewStats {...commonProps} />;
      case 'weaknesses':
        return <WeaknessesStats {...commonProps} />;
      case 'exam':
        return <ExamDayStats {...commonProps} />;
      case 'predictions':
        return <PredictionStats {...commonProps} />;
      default:
        return null;
    }
  };
  
  return (
    <div className={`m_stts1_container ${isDarkMode ? 'm_stts1_dark-mode' : ''}`}>
      <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
      
      <div className="m_stts1_content">
        {/* # Gradient title */}
        <h1 className="m_stts1_gradient_title">Mentee Brain</h1>
        
        {/* # Tabs navigation */}
        <div className="m_stts1_tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`m_stts1_tab ${activeTab === tab.id ? 'm_stts1_active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <tab.icon className="m_stts1_tab_icon" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* # Tab content */}
        <div className={`m_stts1_tab_container ${activeTab === 'qbank' ? 'm_stts1_qbank_active' : ''}`}>
          {renderTabContent()}
        </div>
      </div>
      
      <DashboardFooter isDarkMode={isDarkMode} />
    </div>
  );
}

export default MenteeStats1;
